import {
  AppConfig,
  ApprovalFilter,
  ApprovalFilterType,
  ApprovalState,
  ApprovalTempFilter,
  DocumentListFilters,
  DocumentListParams,
  MyInfo,
  NotificationState,
  OpenState,
  OrgChartState,
  OrgChartStateGlobal,
  RecordDataState,
  RecordFilter,
  RecordMode,
  SalesData,
  SocialLayoutState,
  SocialState,
  VersionInfo,
  WebApprovalState,
} from '@/types/api';
import dayjs from 'dayjs';
import { getFormattedNow } from '@/utils/format';
import { AIMarketingParameterData } from '@/components/AIMarketingExpense/types';
import { DefaultPromptID } from '@/components/Document/constants';

export const INIT_MY_INFO: MyInfo = {
  portalUserKey: '',
  userId: '',
  passportId: '',
  employeeNumber: '',
  name: '',
  nickname: '',
  email: '',
  positionName: '',
  positionCode: '',
  departmentName: '',
  departmentCode: '',
  departmentId: '',
  divisionName: '',
  divisionCode: '',
  companyCode: '',
  companyName: '',
  organizationFullName: '',
  organizationShortName: '',
  profileImageUrl: '',
  phoneInside: '',
  phoneMobile: '',
  birthdayType: 0,
  birthday: '',
  joinedAt: '',
  enterDate: '',
  language: 'ko',
  isLeader: false,
  isPartLeader: false,
  isActingTeamLeader: false,
  job: {
    name: '',
    summary: '',
    content: '',
  },
  isFnCo: false,
  isFnHolding: false,
  roles: [],
  roleIds: [],
  organizationCode: '',
  workingHours: '',
};

export const INIT_APPROVAL_STATE: ApprovalState = {
  headerKey: 0,
  myApprovalKey: 0,
  myWriteKey: 0,
  unRead: false,
};

export const INIT_WEB_APPROVAL_STATE: WebApprovalState = {
  open: true,
  keyword: '',
};

export const INIT_WEB_APPROVAL_FILTER_STATE: ApprovalFilter = {
  id: ApprovalFilterType['전체 문서'],
  sortType: 'authoredAt',
  types: [],
  authorPassportIds: [],
  approverPassportIds: [],
  approverStatuses: [],
  currentApproverPassportIds: [],
  coApproverPassportIds: [],
  coApproverStatuses: [],
  statuses: [],
  referrerPassportIds: [],
  authorDepartmentIds: [],
  approverDepartmentIds: [],
  endAuthoredAt: [],
  unreadPassportIds: [],
  isBookmark: false,
  finalApproverPassportIds: [],
  requireAllApprovers: false,
  requireAllCoApprovers: false,
  keyword: '',
  searchFields: 'subject,content',
  titleKeyword: '',
  contentKeyword: '',
};

export const INIT_WEB_APPROVAL_TEMP_FILTER_STATE: ApprovalTempFilter = {
  types: [],
  authorPassportIds: [],
  approverPassportIds: [],
  currentApproverPassportIds: [],
  coApproverPassportIds: [],
  finalApproverPassportIds: [],
};

export const INIT_APP_SETTINGS: AppConfig = {
  alarmNotice: true,
  dayFind: false,
  seasonFind: false,
  server: 'product',
  language: 'ko',
};

export const INIT_SALES_DATA: SalesData = {
  mainDate: dayjs().subtract(1, 'day'),
  mainSeason: 'ALL',
  rivalDate: dayjs().subtract(1, 'day'),
  analysisDate: dayjs().subtract(1, 'day'),
  analysisSeason: 'ALL',
};

export const INIT_VERSION_INFO: VersionInfo = {
  isTestFlight: false,
  isNeeded: false,
  currentVersion: '',
  latestVersion: '',
  storeUrl: '',
};

export const INIT_SALES_OPEN_STATE: OpenState = {
  main: {
    mainIndex: -1,
    subIndex: -1,
  },
  shopCode: '',
};

export const INIT_SOCIAL_STATE: SocialState = {
  categoryData: [],
  isReserve: false,
  isBookmark: false,
  categoryId: '',
  groupId: '',

  currentDate: getFormattedNow(),
  isTopicRender: false,
};

export const INIT_SOCIAL_LAYOUT_STATE: SocialLayoutState = {
  isRender: false,
  columns: 4,
  isExpanded: true,
  stickyFlexDirection: 'row',
};

export const INIT_ORG_CHART_STATE: OrgChartState = {
  searchText: '',
  searchPassports: [],
  targetSearchIndex: 0,
};

export const INIT_ORG_CHART_STATE_GLOBAL: OrgChartStateGlobal = {
  memberOpen: true,
  isOnlySelectedView: false,
};

export const INIT_NOTIFICATION_STATE: NotificationState = [];

export const INIT_RECORD_STATE: RecordFilter = {
  docId: '',
  mode: RecordMode.NEW,
  subject: '',
  meetingMsSubject: '',
  meetingMsLink: '',
  meetingMsId: '',
  meetingDate: null,
  meetingType: DefaultPromptID,
  meetingTypeId: '0',
  internal: [],
  external: [],
  recordings: [],
  attachments: [],
  relations: [],
  shareAttender: [],
  content: '',
  memo: '',
  searchFields: {
    innerAttender: '',
    externalAttender: '',
    shareAttender: '',
    dataSearch: '',
  },
  createAt: '',
  createdBy: null,
  commentGroupId: '',
  isBookmarked: false,

  // @ref: Ai 요약내용은 회의록과 연관이 있으나, 개인마다 각자 만든 aiSummary를 가져야하므로 Record에서는 제거
  // aiSummaries: {},
};

export const INIT_RECORD_DATA_STATE: RecordDataState = {
  openDraggable: false,
  docId: '',
  title: '',
  recordingData: null,
  isUploading: false,
  uploadProgress: 0,
  visible: true,
};

export const INIT_WEB_DOCUMENT_FILTER_STATE: DocumentListFilters = {
  category: 'all',
  keyword: '',
  listSize: 20,
  currentPage: 1,
  isOnlyShared: 0,
  createdBy: [],
  internalParticipants: [],
  requireAllInternalParticipants: false,
  sortType: 'createdAt',
  orderType: 'desc',
  searchFields: 'subject,content',
  documentTypes: [],
  isRead: null,
  isBookmarked: false,
  createdStartedAt: undefined,
  createdEndedAt: undefined,
  meetingStartedAt: undefined,
  meetingEndedAt: undefined,
};

export const INIT_TALK_FLOW_COUNT_PARAMS: DocumentListParams = {
  ...INIT_WEB_DOCUMENT_FILTER_STATE,
  documentTypes: '',
  createdBy: [],
  internalParticipants: undefined,
  listSize: 100,
  isOnlyShared: 1,
  isRead: false,
};

export const INIT_TALK_FLOW_BOOKMARK_COUNT_PARAMS: DocumentListParams = {
  ...INIT_WEB_DOCUMENT_FILTER_STATE,
  isBookmarked: true,
  documentTypes: '',
  createdBy: [],
  internalParticipants: undefined,
  listSize: 100,
};

export const INIT_AI_MARKETING_DATA_STATE: AIMarketingParameterData = {
  brands: [],
  category: [],
  categoryItemCodes: [],
  criteriaMap: [],
  llms: [],
  sapAccountNames: [],
  sapAccountWithSubItems: [],
  sapContentProducingItems: [],
  criteriaOptions: [],
  seasons: [],
};
